import { useState } from "react";
import { CheckIcon, XMarkIcon, ArrowTopRightOnSquareIcon, MapPinIcon, ClockIcon, UserIcon } from "@heroicons/react/24/outline";
import localPlaceholder from "../../images/def_user_icon.svg";
import { formatListingPrice } from "./utils";
import RejectReasonPicker from "./RejectReasonPicker";

const STATUS = {
  pending: { label: "На модерации", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  active: { label: "Принято", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  rejected: { label: "Отклонено", cls: "bg-rose-500/15 text-rose-500 border-rose-500/30" },
  inactive: { label: "Неактивно", cls: "bg-gray-500/15 text-gray-400 border-gray-500/30" },
};

export function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "только что";
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  if (diff < 172800) return "вчера";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Жинақы жол: модератор бір экранда көп жарнама көріп, бір басумен шешеді. */
export default function ModerationRow({
  ad,
  selected,
  onToggleSelect,
  onOpen,
  onAccept,
  onReject,
  busy,
  getCategoryName,
  getSubcategoryName,
}) {
  const [rejecting, setRejecting] = useState(false);
  const st = STATUS[ad.status] || { label: ad.status, cls: "bg-gray-500/15 text-gray-400 border-gray-500/30" };
  const category = [getCategoryName(ad.category_id), ad.subcategory_id && getSubcategoryName(ad.category_id, ad.subcategory_id)]
    .filter(Boolean)
    .join(" › ");
  const moderator = ad.moderated_by?.name || ad.moderated_by?.email;

  return (
    <li
      className={`relative px-4 py-3 transition-colors ${selected ? "bg-[#4F45E5]/[0.07]" : "hover:bg-bg-tertiary/60"} ${
        busy ? "pointer-events-none opacity-60" : ""
      }`}
    >
      <div className="flex items-center gap-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(ad.id)}
          className="h-4 w-4 shrink-0 cursor-pointer accent-[#4F45E5]"
          aria-label={`Выбрать объявление ${ad.id}`}
        />

        <button type="button" onClick={() => onOpen(ad)} className="shrink-0 overflow-hidden rounded-lg bg-bg-tertiary">
          <img
            src={ad.main_image_url || localPlaceholder}
            alt=""
            loading="lazy"
            className="h-16 w-24 object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = localPlaceholder;
            }}
          />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpen(ad)}
              className="truncate text-left text-sm font-semibold text-text-primary hover:text-[#7C3AED]"
              title={ad.title}
            >
              {ad.title || "Без названия"}
            </button>
            <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
          </div>
          <p className="mt-0.5 truncate text-xs text-text-muted">{ad.description || "Описание отсутствует"}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{formatListingPrice(ad.price, ad.currency, ad.negotiable)}</span>
            {ad.seller && (
              <span className="inline-flex items-center gap-1" title={ad.seller.phone_number || ""}>
                <UserIcon className="h-3.5 w-3.5" />
                {ad.seller.name || ad.seller.phone_number || `#${ad.seller.id}`}
              </span>
            )}
            {category && <span className="truncate">{category}</span>}
            {ad.location?.locality && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="h-3.5 w-3.5" />
                {ad.location.locality}
              </span>
            )}
            <span className="inline-flex items-center gap-1" title={ad.created_at ? new Date(ad.created_at).toLocaleString("ru-RU") : ""}>
              <ClockIcon className="h-3.5 w-3.5" />
              {relativeTime(ad.created_at)}
            </span>
            <span className="text-text-muted">#{ad.id}</span>
            {moderator && ad.status !== "pending" && (
              <span className="text-text-muted">
                модератор: {moderator}
                {ad.moderated_at ? `, ${relativeTime(ad.moderated_at)}` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {busy ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
          ) : (
            <>
              <button
                type="button"
                onClick={() => onAccept(ad.id)}
                disabled={ad.status === "active"}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <CheckIcon className="h-4 w-4" />
                Принять
              </button>
              <button
                type="button"
                onClick={() => setRejecting((v) => !v)}
                disabled={ad.status === "rejected"}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
                  rejecting ? "border-rose-500 bg-rose-500/10 text-rose-500" : "border-border-primary text-rose-500 hover:border-rose-500 hover:bg-rose-500/10"
                }`}
              >
                <XMarkIcon className="h-4 w-4" />
                Отклонить
              </button>
              <button
                type="button"
                onClick={() => onOpen(ad)}
                className="rounded-lg p-2 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
                title="Открыть детали"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {rejecting && !busy && (
        <div className="mt-3 pl-8 sm:pl-[8.5rem]">
          <RejectReasonPicker
            onCancel={() => setRejecting(false)}
            onConfirm={(msg) => {
              setRejecting(false);
              onReject(ad.id, msg);
            }}
          />
        </div>
      )}
    </li>
  );
}
